import { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

type Theme =
  | "dark"
  | "light"
  | "dark-temp"
  | "weblate"
  | "mercedes"
  | "red-bull"
  | "ferrari"
  | "mclaren"
  | "aston-martin"
  | "alpine"
  | "williams"
  | "sauber"
  | "haas";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "vite-ui-theme",
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (Cookies.get(storageKey) as Theme) || defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove(
      "light",
      "dark",
      "dark-temp",
      "weblate",
      "mercedes",
      "red-bull",
      "ferrari",
      "mclaren",
      "aston-martin",
      "alpine",
      "williams",
      "sauber",
      "haas"
    );
    root.classList.add(theme);

    Cookies.set(storageKey, theme);
  }, [theme, storageKey]);

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
